import {Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import {UtilService}    from "../../../shared/util.service";
import {TrendsService} from "./trends.service";
import {Subscription, forkJoin}   from "rxjs";
import {Hotel}   from "../../../shared/model/index";
import { ChartsDataSet } from "../../../shared/utils/charts-data-set";
import {TranslateService} from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { CustomersState } from 'app/states/customers/customers.state';
import { DateService } from 'app/shared/services/date.service';
import { ActivatedRoute } from '@angular/router';
import { skip, distinctUntilChanged, tap, filter } from 'rxjs/operators';

@Component({
    selector: 'mh-inter-chain-trends',
    templateUrl: './trends.component.html',
    styleUrls: ['./trends.component.scss'],
    providers: [TrendsService, ChartsDataSet],
    standalone: false
})

export class TrendsComponent{

  @Select(CustomersState.relativeCurrentChain) customersReady$: Observable<any[]>;

  @Input()
  chain : Hotel[] = [];

  @Input()
  productId = 1;

  @Input()
  showAreas = true;

  @Input()
  showFilters = true;

  @Input()
  trend = 'survey_total_score';

  @Input()
  showDefaultLegends = false;

  @Input()
  isCorporate = true;

  @Input()
  currentDates = null;

  @Input()
  currentTrend;

  @Input()
  areaSelected: any = '';

  segmentedBy = 'months';

  actives:any[] = [];
  data;

  areas: any[] = [];
  waiting = true;

  ignoredKeysOfTable = [];

  @Input() actions: { [key: string | number]: string | number };

  @Output()
  isReady: EventEmitter<boolean> = new EventEmitter();
  averageLabel: string;
  private initialized = false;
  private surveySubscribe: Subscription;
  private customersStateSubscribe: Subscription;
  // lineChart
  parserData = {};
  parserDataLabels:string[];
  lineChartData:Array<any> = [];
  lineChartLabels:Array<any> = [];
  lineChartColors:Array<any> = [];

  polarChartData:Array<any> = [];

  lists: string[] = [];
  areasFound = {};
  firstCall = true;

  constructor(
    private utilService: UtilService,
    private trendsService:TrendsService,
    private translate: TranslateService,
    private chartsDataSet: ChartsDataSet,
    private store: Store,
    private dateService: DateService,
  ) {
    this.translate.get(['commons.average_corporate']).subscribe((result) => {
      this.averageLabel = result['commons.average_corporate'];
    })
  }

  ngOnInit(): void {
    this.surveySubscribe = this.utilService.isCurrentSurveyChanged()
      .pipe(
        distinctUntilChanged(),
        skip(this.chain && this.chain.length > 1 ? 0 : 1),
      )
      .subscribe(() => {
        if (this.currentDates) {
          this.init();  
        }
      });

    this.customersStateSubscribe = this.customersReady$
      .pipe(
        distinctUntilChanged(),
        filter((customer) => customer?.length > 0),
        skip(this.chain && this.chain.length > 1 ? 1 : 0)
      )
      .subscribe((value) => {
        if (this.chain.length === 1 && value.length > 1) {
          return;
        }

        if (this.currentDates) {
          this.init();
        }
      });

    this.init();
    this.initialized = true;
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {    
    if (!this.initialized) return;

    if (simpleChanges.currentDates && !simpleChanges.currentDates.firstChange) {
      this.init();
    }

    if (simpleChanges.currentTrend && !simpleChanges.currentTrend.firstChange) {
      this.trend = simpleChanges.currentTrend.currentValue.value;
      this.init();
    }

    if (simpleChanges.areaSelected && !simpleChanges.areaSelected.firstChange) {
      this.areaSelected = simpleChanges.areaSelected.currentValue;
      this.setGraph();
    }
  }

  ngOnDestroy(){
    this.surveySubscribe?.unsubscribe();
    this.customersStateSubscribe?.unsubscribe();
  }

  reset(){
    this.data = {};
    this.areas  = [];
    this.areaSelected = null;
    this.actives = this.chain.map(x=>x.id);
  }

  init(){
    this.reset();
    this.setList();
    this.waiting = true;
    this.isReady.emit(false);
    this.segmentedBy = this.dateService.getSegmentation();
    this.loadData();
  }

  setList(){
    this.lists = Object.keys(this.actions);
    if(!this.showAreas) {
      this.lists = this.lists.filter((trend) => trend !== 'ranking_areas');
    }
    if(this.utilService.customerIsRetail || this.utilService.customerIsClinic){
      this.lists = this.lists.filter(x=>x!=='ranking')
    }
  }

  loadData(){ 
    const language  = this.utilService.getLanguageId();
    const survey    = this.utilService.getCurrentSurvey(this.chain.map(x=>parseInt(x.id)));
    const surveyIds = survey?.ids || '';
    const version = survey?.survey[0] && survey?.survey[0].version;

    const chain     = survey?.chain || this.chain.map(x=>x.id).join(',');

    const customers = !this.isCorporate ?
      chain :
      (this.chain ? this.chain.map((c) => c.id).join(',') : this.store.selectSnapshot(CustomersState.relativeCurrentChain).map((c) => c.id).join(','));

    if (customers.length) {
      forkJoin([
        this.trendsService[this.actions[this.trend]](this.date, customers,this.productId, language, this.segmentedBy, surveyIds, version)
      ]).subscribe((result:any[])=>{
        this.data = result[0];
        if(this.trend === 'ranking_areas') this.calculateTotalsAreas();
        this.setGraph();
        this.isReady.emit(true);
        this.waiting = false;
        this.firstCall = false;
      },error=>{
        console.error(error);
      })
    }

  }

  canLoadWidget(){
    if(!this.utilService.currentUser.isSuperAdmin()) return true;
    if(this.utilService.currentUser.fakeUser) return true;
    return false;
  }

  setGraph(){
    if (!this.data) return;
    this.ignoredKeysOfTable = [];
    const dataValue = this.trend ==='ranking' ? this.data : this.data['values'] ;
    const dataAverage = this.data['average'];
    if (dataValue === undefined || dataValue === null) return;
    const dataLabels = this.utilService.getIntervalLabels(
      this.utilService.stringDateToDate(this.currentDates.startDate),
      this.utilService.stringDateToDate(this.currentDates.endDate),
      this.segmentedBy
    );
    this.parserData = {}

    if (this.trend === 'rate' && this.chain.length === 1) {
      let emailLabel = '';
      let whatsappLabel = '';
      let openLabel = '';
      let rateLabel = '';
      this.translate.get([
        `widgets.trends.filters.email_surveys_individual`,
        `widgets.trends.filters.whatsapp_surveys_individual`,
        `widgets.trends.filters.open_surveys_individual`,
        `widgets.trends.filters.general`
      ]).subscribe((result) => {
        emailLabel = result[`widgets.trends.filters.email_surveys_individual`];
        whatsappLabel = result[`widgets.trends.filters.whatsapp_surveys_individual`];
        openLabel = result[`widgets.trends.filters.open_surveys_individual`];
        rateLabel = result[`widgets.trends.filters.general`];
      });
      dataValue.forEach(data => {
        if (this.actives.includes(data.customer_id)) {
          const rateData = this.getDataTrend(data, false, 'rate');
          this.parserData[rateLabel] = { data: rateData, selector: data.customer_id + '_rate', isAverage: false, isDashed: true };
          const emailData = this.getDataTrend(data, false, 'email_surveys');
          this.parserData[emailLabel] = { data: emailData, selector: data.customer_id + '_email' };
          const whatsappData = this.getDataTrend(data, false, 'whatsapp_surveys');
          this.parserData[whatsappLabel] = { data: whatsappData, selector: data.customer_id + '_whatsapp' };
          const openData = this.getDataTrend(data, false, 'open_surveys');
          this.parserData[openLabel] = { data: openData, selector: data.customer_id + '_open' };
        }
      });
      if (this.data['average'] && this.chain.length > 1) {
        const rateAvg = this.getDataTrend(this.data['average'], true, 'rate');
        this.parserData[rateLabel] = { data: rateAvg, selector: 'average_rate', isAverage: true, isDashed: true };
        const emailAvg = this.getDataTrend(this.data['average'], true, 'email_surveys');
        this.parserData[emailLabel] = { data: emailAvg, selector: 'average_email' };
        const whatsappAvg = this.getDataTrend(this.data['average'], true, 'whatsapp_surveys');
        this.parserData[whatsappLabel] = { data: whatsappAvg, selector: 'average_whatsapp' };
        const openAvg = this.getDataTrend(this.data['average'], true, 'open_surveys');
        this.parserData[openLabel] = { data: openAvg, selector: 'average_open' };
      }
      this.parserDataLabels = dataLabels.map((x) => x.format);
      const lineDataSet = this.chartsDataSet.getLineData(this.parserData, this.parserDataLabels);
      if (lineDataSet.data) {
        lineDataSet.data.forEach(ds => {
          if (ds.label && ds.label.includes(rateLabel)) {
            ds.borderDash = [5, 5];
          }
        });
      }
      this.lineChartData = this.orderDataSet(lineDataSet["data"]);
      
      this.lineChartLabels = lineDataSet["labels"];
      this.lineChartColors = this.orderColorsDataSet(lineDataSet["colors"]);
    } else if (this.trend === 'answered' && this.chain.length === 1) {
      let emailLabel = '';
      let whatsappLabel = '';
      let openLabel = '';
      let answeredLabel = '';
      this.translate.get([
        `widgets.trends.filters.email_surveys_individual`,
        `widgets.trends.filters.whatsapp_surveys_individual`,
        `widgets.trends.filters.open_surveys_individual`,
        `widgets.trends.filters.total`
      ]).subscribe((result) => {
        emailLabel = result[`widgets.trends.filters.email_surveys_individual`];
        whatsappLabel = result[`widgets.trends.filters.whatsapp_surveys_individual`];
        openLabel = result[`widgets.trends.filters.open_surveys_individual`];
        answeredLabel = result[`widgets.trends.filters.total`];
        this.ignoredKeysOfTable = [answeredLabel];
      });
      dataValue.forEach(data => {
        if (this.actives.includes(data.customer_id)) {
          const answeredData = this.getDataTrend(this.data['average'], true);
          this.parserData[answeredLabel] = { data: answeredData, selector: data.customer_id + '_answered' };
          const emailData = this.getDataTrend(data, false, 'email_surveys.answered');
          this.parserData[emailLabel] = { data: emailData, selector: data.customer_id + '_email_answered' };
          const whatsappData = this.getDataTrend(data, false, 'whatsapp_surveys.answered');
          this.parserData[whatsappLabel] = { data: whatsappData, selector: data.customer_id + '_whatsapp_answered' };
          const openData = this.getDataTrend(data, false, 'open_surveys.answered');
          this.parserData[openLabel] = { data: openData, selector: data.customer_id + '_open_answered' };
        }
      });
      if (this.data['average'] && this.chain.length > 1) {
        this.parserData[answeredLabel] = { data: this.getDataTrend(this.data['average'], true, 'answered'), selector: 'average_answered' };
        this.parserData[emailLabel] = { data: this.getDataTrend(this.data['average'], true, 'email_surveys.answered'), selector: 'average_email_answered' };
        this.parserData[whatsappLabel] = { data: this.getDataTrend(this.data['average'], true, 'whatsapp_surveys.answered'), selector: 'average_whatsapp_answered' };
        this.parserData[openLabel] = { data: this.getDataTrend(this.data['average'], true, 'open_surveys.answered'), selector: 'average_open_answered' };
      }
      this.parserDataLabels = dataLabels.map((x) => x.format);
      const lineDataSet = this.chartsDataSet.getLineData(this.parserData, this.parserDataLabels);
      this.lineChartData = this.orderDataSet(lineDataSet["data"]);
      this.lineChartLabels = lineDataSet["labels"];
      this.lineChartColors = this.orderColorsDataSet(lineDataSet["colors"]);
    } else if (this.trend === 'sent' && this.chain.length === 1) {
      let emailLabel = '';
      let whatsappLabel = '';
      let sentLabel = '';
      this.translate.get([
        `widgets.trends.filters.email_surveys_individual`,
        `widgets.trends.filters.whatsapp_surveys_individual`,
        `widgets.trends.filters.total`
      ]).subscribe((result) => {
        emailLabel = result[`widgets.trends.filters.email_surveys_individual`];
        whatsappLabel = result[`widgets.trends.filters.whatsapp_surveys_individual`];
        sentLabel = result[`widgets.trends.filters.total`];
        this.ignoredKeysOfTable = [sentLabel];
      dataValue.forEach(data => {
        if (this.actives.includes(data.customer_id)) {
          const sentData = this.getDataTrend(data, false, 'overall_sent');
          this.parserData[sentLabel] = { data: sentData, selector: data.customer_id + '_overall_sent' };
          const emailData = this.getDataTrend(data, false, 'email_surveys.sent');
          this.parserData[emailLabel] = { data: emailData, selector: data.customer_id + '_email_sent' };
          const whatsappData = this.getDataTrend(data, false, 'whatsapp_surveys.sent');
          this.parserData[whatsappLabel] = { data: whatsappData, selector: data.customer_id + '_whatsapp_sent' };
        }
      });
      this.parserDataLabels = dataLabels.map((x) => x.format);
      const lineDataSet = this.chartsDataSet.getLineData(this.parserData, this.parserDataLabels);
      this.lineChartData = this.orderDataSet(lineDataSet["data"]);
      this.lineChartLabels = lineDataSet["labels"];
      this.lineChartColors = this.orderColorsDataSet(lineDataSet["colors"]);
    });
    } else if (Array.isArray(dataValue)) {
      dataValue.forEach(data=>{
        if(this.actives.includes(data.customer_id)){
          const customer: any = this.chain.find(x=>parseInt(x.id)===data.customer_id);
          const averageDataLabel = this.translate.instant('commons.average');
          if (dataAverage) this.parserData[averageDataLabel] = {data: this.getDataTrend(dataAverage, true), selector: 'average'};
          this.parserData[customer?.name] = {data: this.getDataTrend(data), selector: data.customer_id }
        }
      });

      this.parserDataLabels = dataLabels.map((x: any) => x.format);
      const lineDataSet      = this.chartsDataSet.getLineData(this.parserData, this.parserDataLabels);
  
      this.lineChartData     = this.orderDataSet(lineDataSet["data"]);
      this.lineChartLabels   = lineDataSet["labels"];
      this.lineChartColors   = this.orderColorsDataSet(lineDataSet["colors"]);
    }
  }

  orderDataSet(lineChartData) {
    lineChartData.sort((x, y) => {
      return x.isAverage ? -1 : 1;
    });
    return lineChartData;
  }

  orderColorsDataSet(colors) {
    colors.sort((x, y) => {
      return x.borderDash.length > 0 ? -1 : 1;
    });
    return colors;
  }

  calculateTotalsAreas(){
    const dataPeriods = this.data.values.map(x=>x.periods);
    dataPeriods.forEach(periods=>{
      this.getAveragePeriods(periods);
    })

    const dataAverage = this.data.average.periods
    this.getAveragePeriods(dataAverage)

  }

  getAveragePeriods(periods){
    periods.forEach(period=>{
      const data = period.data.ranking_areas;
      let weightedSum: any   = null;
      let qtyAnswer     = null;

      data.areas.forEach(area=>{
        const weightVal = area.note && area.answers ? area.note * area.answers : null;
        weightedSum     = weightVal ? weightedSum + weightVal : weightedSum;
        qtyAnswer       = area.answers ? qtyAnswer + area.answers : qtyAnswer;
      })

      data.areas.push({
        id_area: -1,
        note: weightedSum && qtyAnswer ? weightedSum / qtyAnswer : null,
        answers: qtyAnswer
      })

    })
  }

  setSelectlValue(option, key){
    if(key==='areas'){
      this.areaSelected = option.value;
      this.setGraph();
    }else{
      this[key] = option.value;
      this.init();
    }
  }

  filterCustomers($event){
    this.actives = $event;
  }

  getDataTrend(data, isAverage=false, trend?: string){
    let keyData = trend || this.trend;

    if (/_answered$/.test(keyData)) {
      keyData = keyData.replace('_answered', '.answered');
    }

    if(keyData==='pms'){
      return data.periods.map(x=>{
        const percentage = x.data[keyData].percentage;
        return percentage ? percentage.value : null;
      });
    } else if (keyData === 'total_guests') {
      return data.periods.map((period: any) => period.data?.pms?.total_count ?? null);
    } else if(keyData==='ranking_areas'){
      const allData = data.periods.map(x=>x.data[keyData]);
      if(this.areas.length===0){
        let areas = [];
        allData.forEach(period=>{
          areas = areas.concat(period.areas.map(x=>x.id_area));
        })
        this.areas        = this.utilService.uniqArray(areas).sort((a, b) => a - b);
        this.areaSelected = this.areas[0];
      }
      return allData.map(x=>x.areas.find(a=>a.id_area===this.areaSelected)).map(x=>{
        return x && x['note'] ? x.note : null
      });
    } else if (
      ['rate', 'answered', 'sent', 'overall_sent', 'email_surveys', 'open_surveys', 'whatsapp_surveys'].includes(keyData)
    ) {
      return data.periods.map((period) => {
        let rate;
        if (keyData === 'sent' || keyData === 'overall_sent') {
          rate = period?.data?.rates?.[keyData] || null;
        } else if (['email_surveys', 'open_surveys', 'whatsapp_surveys'].includes(keyData)) {
          rate = period?.data?.rates[keyData]?.rate || null
        } else {
          rate = period?.data?.rates[keyData] || null
        }
        return rate?.actual ?? null;
      });
    } else if (
      ['email_surveys.answered', 'open_surveys.answered', 'whatsapp_surveys.answered'].includes(keyData)
    ) {
      const [subKey, answeredKey] = keyData.split('.');
      return data.periods.map((period) => {
        const answered = period?.data?.rates?.[subKey]?.[answeredKey];
        return answered?.actual ?? null;
      });
    } else if (
      ['email_surveys.sent', 'whatsapp_surveys.sent'].includes(keyData)
    ) {
      const [subKey, sentKey] = keyData.split('.');
      return data.periods.map((period) => {
        const sent = period?.data?.rates?.[subKey]?.[sentKey];
        return sent?.actual ?? null;
      });
    } else if (['promoters', 'passives', 'detractors'].includes(keyData)) {
      return data.periods.map((period: any) => period.data?.details?.[keyData]?.count ?? null);
    } else if (keyData === 'survey_total_score') {
      return data.periods.map((period: any) => period.data?.qualification ?? null);
    } else if (keyData === 'semantic_index') {
      return data.periods.map((period: any) => {
        const semanticIndex = period.data?.semantic_index_data.find(
          semanticIndexByProduct => semanticIndexByProduct.product_id === this.productId,
        );
        return semanticIndex?.semantic_index ?? null
      });
    } else {
      return data.periods.map(x=>x.data[keyData]);
    }
  }

  get date() {
    return {
      startDate: this.currentDates.startDate,
      endDate: this.currentDates.endDate
    }
  }

}
